import type { RefObject } from 'react';
import { FolderPlusIcon, UploadIcon } from 'lucide-react';
import { Dropzone } from '@/shared/ui/Dropzone/Dropzone';
import { PROJECT_PERMISSIONS } from '@inference.engine/permissions/web';
import { useHasProjectPermission } from '@/entities/user';
import { ProjectFileUploadGuard } from '@/features/project-file-upload-guard';
import { useFileUploadToFolder } from '../../hooks/useFileUploadToFolder';
import { useTranslation } from 'react-i18next';

interface Props {
  projectId: string;
  folderId: string;
  /**
   * Bound to a hidden file input so the pane header's upload button opens the
   * same picker the dropzone does, without a second upload pipeline.
   */
  uploadInputRef?: RefObject<HTMLInputElement | null>;
}

/**
 * A folder with neither assets nor subfolders, which can still become either.
 * The subfolder half of that choice is offered by the pane header; this owns
 * the upload half.
 *
 * Carries no entrance of its own. The pane dissolves its placeholder into this
 * view, and a second staggered arrival on top of that read as the folder taking
 * half a second to answer.
 */
export function EmptyFolderPage(props: Props) {
  const { t } = useTranslation('projects');

  const canUpload = useHasProjectPermission([PROJECT_PERMISSIONS.ProjectUploadAssets]);
  const { startUpload, isError, resetError } = useFileUploadToFolder(
    props.projectId,
    props.folderId,
  );

  return (
    <ProjectFileUploadGuard
      projectId={props.projectId}
      folderId={props.folderId}
      onResolved={startUpload}
    >
      {({ submit }) => (
        <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-xl overflow-y-auto">
          <input
            ref={props.uploadInputRef}
            type="file"
            multiple
            hidden
            onChange={(e) => {
              if (e.target.files) {
                submit(Array.from(e.target.files));
                e.target.value = '';
              }
            }}
          />

          <div className="flex flex-col items-center gap-xs text-center">
            <FolderPlusIcon className="size-6 text-foreground" />
            <h2 className="text-body-lg font-medium text-foreground">
              {t('projectDetail.emptyFolderPage.thisFolderIsEmpty')}
            </h2>
            <p className="text-body-md text-muted-foreground">
              {t('projectDetail.emptyFolderPage.addFoldersInsideItOrUpload')}
            </p>
          </div>

          {/* The drop target is an upload affordance like the header button, so it
              answers to the same permission. Without it the folder still says it is
              empty, it just does not offer a way to fill it. */}
          {canUpload && (
            <div className="w-[420px] max-w-full">
              <Dropzone
                onDrop={submit}
                title={t('projectDetail.emptyFolderPage.clickToUploadOrDrag')}
                description={t('projectDetail.emptyFolderPage.imagesVideosAudioAndPdf')}
                icon={<UploadIcon className="size-5 text-primary" />}
              />
            </div>
          )}

          {isError && (
            <button
              type="button"
              onClick={resetError}
              className="text-body-sm text-destructive hover:underline"
            >
              {t('projectDetail.emptyFolderPage.uploadFailedClickToDismiss')}
            </button>
          )}
        </div>
      )}
    </ProjectFileUploadGuard>
  );
}
